import { vetor, type Vetor3 } from '../../dominio/geometria/vetor3'
import type {
  AncoraOcupante,
  ConfiguracaoCinto,
  PerfilVeiculo,
} from '../../dominio/didatico/perfilVeiculo'
import { sufixoAncora } from '../../dominio/didatico/perfilVeiculo'
import { pontosDoCinto } from '../../dominio/didatico/pontosCinto'
import { componenteDidatico } from '../../dominio/didatico/catalogo'
import { CORES_SEMANTICAS } from '../../dominio/didatico/tokens'
import { Barra } from '../componentes/Barra'
import { CaixaArredondada } from '../componentes/CaixaArredondada'
import { MaterialPeca, Peca } from '../componentes/Peca'
import { Rotulo3D } from '../componentes/Rotulo3D'
import { useMaquete } from '../componentes/contextoMaquete'

interface Props {
  perfil: PerfilVeiculo
  ancora: AncoraOcupante
  config: ConfiguracaoCinto
}

/** fita de poliéster: cinza-grafite, como a da maioria dos veículos */
const COR_FITA = '#2d3138'
const COR_PLASTICO = '#1b1d21'
const COR_BOTAO = '#c0262d'

/**
 * Conjunto de retenção de uma posição de ocupante.
 *
 * A geometria é derivada da âncora e da fila, então a mesma implementação
 * serve dianteiro e traseiro, hatch e sedan. O tipo do cinto e a presença dos
 * pretensionadores vêm do perfil didático — não são assumidos.
 *
 * Fita, fecho e retrator têm a cor real; os pretensionadores ficam com a cor
 * semântica de retenção, porque são a carga ativa que o aluno precisa achar.
 */
export function CintoSeguranca({ perfil, ancora, config }: Props) {
  const { mostrarRotulos, camadas, retencaoEmEvidencia, semCintoAncoras } = useMaquete()
  // o ocupante está sem cinto: a faixa não é desenhada, mas a peça continua
  // existindo no veículo — não é um estado de `estadoPecas`
  if (semCintoAncoras.includes(ancora.id)) return null
  const sufixo = sufixoAncora(ancora.id)
  const idCinto = `cinto${sufixo}`
  const idPretensionador = `pretensionador${sufixo}`
  const idPretensionadorFecho = `pretensionadorFecho${sufixo}`
  const idRetrator = `retrator${sufixo}`
  const idAncoragens = `ancoragens${sufixo}`

  // a geometria vem do domínio, que é onde ela está sob teste
  const pontos = pontosDoCinto(perfil, ancora, config)
  const largura = retencaoEmEvidencia ? 0.06 : 0.048
  const faixa = (de: Vetor3, para: Vetor3, chave: string) => (
    <Barra key={chave} id={idCinto} de={de} para={para} largura={largura} espessura={0.006} corBase={COR_FITA} />
  )
  // lado da parede do ocupante: +1 à esquerda (+x), -1 à direita
  const ladoParede = Math.sign(pontos.ancoragemInferiorExterna.x) || 1
  const rotuloEm = (p: Vetor3): [number, number, number] => [p.x - ladoParede * 0.08, p.y + 0.14, p.z]

  return (
    <group>
      <Peca id={idCinto}>
        {/* faixa diagonal: só existe no cinto de três pontos */}
        {pontos.ancoragemSuperior && pontos.ombro && [
          pontos.retrator && faixa(pontos.retrator, pontos.ancoragemSuperior, 'coluna'),
          faixa(pontos.ancoragemSuperior, pontos.ombro, 'diagonal-alta'),
          faixa(pontos.ombro, pontos.fecho, 'diagonal-baixa'),
        ]}
        {/* faixa pélvica: existe nos dois tipos */}
        {faixa(pontos.fecho, pontos.quadrilExterno, 'pelvica-interna')}
        {faixa(pontos.quadrilExterno, pontos.ancoragemInferiorExterna, 'pelvica-externa')}

        {pontos.ancoragemSuperior && pontos.saidaNaPrateleira && (
          <FendaPrateleira id={idCinto} posicao={pontos.ancoragemSuperior} />
        )}
        {pontos.ancoragemSuperior && !pontos.saidaNaPrateleira && (
          <GuiaSuperior
            id={idCinto}
            posicao={pontos.ancoragemSuperior}
            ladoParede={ladoParede}
            regulavel={ancora.fila === 'dianteira'}
          />
        )}
        <FechoELingueta id={idCinto} fecho={pontos.fecho} ancoragem={pontos.ancoragemInferiorInterna} />
      </Peca>

      <Peca id={idAncoragens}>
        {/* a fenda da prateleira não é ponto aparafusado: o parafuso é do retrator, por baixo */}
        {!pontos.saidaNaPrateleira && pontos.ancoragemSuperior && (
          <Parafuso id={idAncoragens} posicao={pontos.ancoragemSuperior} ladoParede={ladoParede} />
        )}
        <Parafuso id={idAncoragens} posicao={pontos.ancoragemInferiorExterna} ladoParede={ladoParede} />
        {/* a interna fica no túnel ou no banco: a cabeça aponta para a parede */}
        <Parafuso id={idAncoragens} posicao={pontos.ancoragemInferiorInterna} ladoParede={-ladoParede} />
      </Peca>

      {pontos.retrator && (
        <Peca id={idRetrator}>
          <Retrator id={idRetrator} posicao={pontos.retrator} ladoParede={ladoParede} />
        </Peca>
      )}

      {pontos.pretensionador && (
        <Peca id={idPretensionador}>
          {/* gerador de gás do pretensionador, encostado no carretel */}
          <mesh position={[pontos.pretensionador.x - ladoParede * 0.012, pontos.pretensionador.y - 0.03, pontos.pretensionador.z + 0.05]}>
            <cylinderGeometry args={[0.016, 0.016, 0.13, 14]} />
            <MaterialPeca id={idPretensionador} metalico={0.5} rugosidade={0.35} />
          </mesh>
          {retencaoEmEvidencia && <Halo posicao={pontos.pretensionador} />}
          {mostrarRotulos && camadas.retencao && <RotuloRetencao id={idPretensionador} posicao={rotuloEm(pontos.pretensionador)} />}
        </Peca>
      )}

      {pontos.pretensionadorFecho && (
        <Peca id={idPretensionadorFecho}>
          <PretensionadorFecho id={idPretensionadorFecho} de={pontos.pretensionadorFecho} fecho={pontos.fecho} />
          {retencaoEmEvidencia && <Halo posicao={pontos.pretensionadorFecho} />}
          {mostrarRotulos && camadas.retencao && (
            <RotuloRetencao id={idPretensionadorFecho} posicao={rotuloEm(pontos.pretensionadorFecho)} />
          )}
        </Peca>
      )}
    </group>
  )
}

function Halo({ posicao }: { posicao: Vetor3 }) {
  return (
    <mesh position={[posicao.x, posicao.y, posicao.z]}>
      <sphereGeometry args={[0.1, 14, 12]} />
      <meshBasicMaterial color={CORES_SEMANTICAS.retencao} transparent opacity={0.22} />
    </mesh>
  )
}

/** Guia em D na coluna; na frente, sobre o trilho do regulador de altura. */
function GuiaSuperior({ id, posicao, ladoParede, regulavel }: {
  id: string
  posicao: Vetor3
  ladoParede: number
  regulavel: boolean
}) {
  const x = posicao.x - ladoParede * 0.012
  return (
    <group>
      {regulavel && (
        <mesh position={[posicao.x - ladoParede * 0.004, posicao.y - 0.03, posicao.z]}>
          <boxGeometry args={[0.008, 0.16, 0.03]} />
          <MaterialPeca id={id} corBase="#8a9099" acabamento="metal" />
        </mesh>
      )}
      <mesh position={[x, posicao.y, posicao.z]}>
        <CaixaArredondada args={[0.016, 0.075, 0.06]} raio={0.007} />
        <MaterialPeca id={id} corBase={COR_PLASTICO} />
      </mesh>
      {/* fenda por onde a fita passa */}
      <mesh position={[x - ladoParede * 0.009, posicao.y - 0.012, posicao.z]}>
        <boxGeometry args={[0.003, 0.012, 0.05]} />
        <MaterialPeca id={id} corBase="#c8ccd2" acabamento="metal" />
      </mesh>
    </group>
  )
}

/** Acabamento da fenda na prateleira traseira, por onde a fita sai do retrator. */
function FendaPrateleira({ id, posicao }: { id: string; posicao: Vetor3 }) {
  return (
    <group position={[posicao.x, posicao.y, posicao.z]}>
      <mesh>
        <CaixaArredondada args={[0.085, 0.012, 0.045]} raio={0.005} />
        <MaterialPeca id={id} corBase={COR_PLASTICO} />
      </mesh>
      <mesh position={[0, 0.004, 0]}>
        <boxGeometry args={[0.058, 0.006, 0.008]} />
        <MaterialPeca id={id} corBase="#c8ccd2" acabamento="metal" />
      </mesh>
    </group>
  )
}

/** Fecho no lado do túnel, com botão vermelho, e a lingueta metálica encaixada. */
function FechoELingueta({ id, fecho, ancoragem }: { id: string; fecho: Vetor3; ancoragem: Vetor3 }) {
  return (
    <group>
      {/* haste semirrígida do fecho até a ancoragem no trilho do banco */}
      <Barra id={id} de={ancoragem} para={vetor(fecho.x, fecho.y - 0.035, fecho.z)} largura={0.03} espessura={0.012} corBase={COR_PLASTICO} />
      <mesh position={[fecho.x, fecho.y - 0.01, fecho.z]}>
        <CaixaArredondada args={[0.036, 0.075, 0.055]} raio={0.012} />
        <MaterialPeca id={id} corBase={COR_PLASTICO} />
      </mesh>
      <mesh position={[fecho.x, fecho.y + 0.03, fecho.z + 0.012]}>
        <boxGeometry args={[0.024, 0.008, 0.02]} />
        <MaterialPeca id={id} corBase={COR_BOTAO} />
      </mesh>
      {/* lingueta: placa metálica que sai da fita e entra no fecho */}
      <mesh position={[fecho.x, fecho.y + 0.045, fecho.z - 0.006]}>
        <boxGeometry args={[0.006, 0.05, 0.042]} />
        <MaterialPeca id={id} corBase="#d4d8de" acabamento="metal" />
      </mesh>
    </group>
  )
}

/** Carretel com mola: carcaça, tambor com a fita enrolada e a trava. */
function Retrator({ id, posicao, ladoParede }: { id: string; posicao: Vetor3; ladoParede: number }) {
  const { x, y, z } = posicao
  return (
    <group>
      <mesh position={[x, y, z]}>
        <CaixaArredondada args={[0.05, 0.12, 0.08]} raio={0.01} />
        <MaterialPeca id={id} corBase="#3a3f47" />
      </mesh>
      {/* tambor com a fita enrolada, eixo longitudinal */}
      <mesh position={[x - ladoParede * 0.03, y + 0.02, z]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.03, 0.03, 0.055, 18]} />
        <MaterialPeca id={id} corBase={COR_FITA} />
      </mesh>
      {/* tampa da mola e da trava inercial */}
      <mesh position={[x - ladoParede * 0.03, y + 0.02, z - 0.032]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.034, 0.034, 0.012, 18]} />
        <MaterialPeca id={id} corBase={COR_PLASTICO} />
      </mesh>
    </group>
  )
}

/** Tubo do pretensionador de fecho, do trilho do banco até a base do fecho. */
function PretensionadorFecho({ id, de, fecho }: { id: string; de: Vetor3; fecho: Vetor3 }) {
  return (
    <group>
      <mesh position={[de.x, de.y, de.z]}>
        <cylinderGeometry args={[0.02, 0.02, 0.06, 14]} />
        <MaterialPeca id={id} metalico={0.5} rugosidade={0.35} />
      </mesh>
      <Barra id={id} de={de} para={vetor(fecho.x, fecho.y - 0.05, fecho.z)} largura={0.022} espessura={0.022} cilindrica />
    </group>
  )
}

/** Cabeça de parafuso com arruela, apontando para dentro do habitáculo. */
function Parafuso({ id, posicao, ladoParede }: { id: string; posicao: Vetor3; ladoParede: number }) {
  // girado em z, o eixo do cilindro (+y local) aponta para -x no mundo:
  // +ladoParede no eixo local é "para dentro", longe da parede
  return (
    <group position={[posicao.x, posicao.y, posicao.z]} rotation={[0, 0, Math.PI / 2]}>
      <mesh>
        <cylinderGeometry args={[0.022, 0.022, 0.004, 18]} />
        <MaterialPeca id={id} acabamento="metal" corBase="#b9bec6" />
      </mesh>
      <mesh position={[0, ladoParede * 0.007, 0]}>
        <cylinderGeometry args={[0.014, 0.014, 0.01, 6]} />
        <MaterialPeca id={id} acabamento="metal" corBase="#9aa0a8" />
      </mesh>
    </group>
  )
}

/**
 * Rótulo do pretensionador. Enquanto a abreviação não for confirmada pelo
 * instrutor, mostra o nome por extenso em vez de inventar sigla.
 */
function RotuloRetencao({ id, posicao }: { id: string; posicao: [number, number, number] }) {
  const componente = componenteDidatico(id)
  if (!componente) return null
  return (
    <Rotulo3D
      posicao={posicao}
      texto={componente.sigla ?? 'pretensionador'}
      variante="retencao"
      fator={3.6}
    />
  )
}
